<template>
    <time ref="el" class="mk-time" :title="absolute">
        <template v-if="invalid">{{ $t("ago-invalid") }}</template>
        <template v-else-if="mode === 'relative'">{{ relative }}</template>
        <template v-else-if="mode === 'absolute'">{{ absolute }}</template>
        <template v-else-if="mode === 'detail'"
            >{{ absolute }} ({{ relative }})
        </template>
    </time>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef, watch } from "vue";
import { dateFormatter, relativeTimeFormatter } from "@/types/time";
import { useFluent } from "fluent-vue";
import {
    differenceInCalendarYears,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInWeeks,
} from "date-fns";
import { useEventListener, useIntervalFn } from "@vueuse/core";

const el = useTemplateRef("el");
const { $t } = useFluent();

const props = withDefaults(
    defineProps<{
        time: Date | string | number | null;
        origin?: Date | null;
        mode?: "relative" | "absolute" | "detail";
    }>(),
    {
        mode: "relative",
    }
);

const _time =
    props.time == null
        ? NaN
        : typeof props.time === "number"
        ? props.time
        : (props.time instanceof Date
              ? props.time
              : new Date(props.time)
          ).getTime();
const invalid = Number.isNaN(_time);
const absolute = !invalid
    ? dateFormatter({
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
      }).format(_time)
    : $t("ago-invalid");

const now = ref((props.origin ?? new Date()).getTime());
const relative = ref<string>("");

watch(
    now,
    (_now) => {
        if (props.mode === "absolute") {
            return;
        }

        if (invalid) {
            relative.value = $t("ago-invalid");
            return;
        }

        const interval =
            _now > _time
                ? { start: _time, end: _now }
                : { start: _now, end: _time };

        const sign = _now > _time ? -1 : 1;

        const diffYears = differenceInCalendarYears(
            interval.end,
            interval.start
        );
        const diffMonths = differenceInMonths(interval.end, interval.start);
        const diffWeeks = differenceInWeeks(interval.end, interval.start);
        const diffDays = differenceInDays(interval.end, interval.start);
        const diffHours = differenceInHours(interval.end, interval.start);
        const diffMinutes = differenceInMinutes(interval.end, interval.start);
        const diffSeconds = differenceInSeconds(interval.end, interval.start);

        const relativeFormatter = relativeTimeFormatter({
            numeric: "always",
            style: "narrow",
        });

        if (diffYears) {
            relative.value = relativeFormatter.format(sign * diffYears, "year");
        } else if (diffMonths) {
            relative.value = relativeFormatter.format(
                sign * diffMonths,
                "months"
            );
        } else if (diffWeeks) {
            relative.value = relativeFormatter.format(sign * diffWeeks, "week");
        } else if (diffDays) {
            relative.value = relativeFormatter.format(sign * diffDays, "day");
        } else if (diffHours) {
            relative.value = relativeFormatter.format(sign * diffHours, "hour");
        } else if (diffMinutes) {
            relative.value = relativeFormatter.format(
                sign * diffMinutes,
                "minute"
            );
        } else if (diffSeconds) {
            relative.value = relativeFormatter.format(
                sign * diffSeconds,
                "second"
            );
        } else {
            relative.value = $t("ago-justNow");
        }
    },
    {
        immediate: true,
    }
);

const { pause, resume } = useIntervalFn(
    () => {
        if (!props.origin) {
            now.value = new Date().getTime();
        }
    },
    15000 + Math.random() * 5000,
    {
        immediate: true,
        immediateCallback: true
    }
);

useEventListener(el, "contentvisibilityautostatechange", (e) => {
    if (e.skipped) {
        pause();
    } else {
        resume();
    }
});
</script>
<style lang="scss" scoped>
.mk-time {
    content-visibility: auto;
    line-height: 1em;
}
</style>